package kz.dentalcrm.app.screens.services.services

import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.base.Selectable
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.data.network.response_model.services.ServiceItem
import kz.dentalcrm.app.domain.ServicesRepository
import kz.dentalcrm.app.screens.services.services.mvi.ServicesState
import kz.dentalcrm.app.screens.services.services.mvi.ServicesViewEvent
import kz.dentalcrm.app.screens.services.services.mvi.ServicesViewSideEffect
import org.koin.core.component.inject

class ServicesViewModel : BaseStateViewModel<ServicesViewEvent, ServicesState, ServicesViewSideEffect>() {

    private val servicesRepository: ServicesRepository by inject()

    override fun setInitialState() = ServicesState()

    private var currentPage: Int = 1
    private var _isLastPage: Boolean = false

    private val serviceList = mutableListOf<ServiceItem>()

    override fun handleEvents(event: ServicesViewEvent) {
        when (event) {
            is ServicesViewEvent.SearchService -> {
                _isLastPage = false
                currentPage = 1
                serviceList.clear()
                setState {
                    copy(
                        serviceCategories = viewState.value.serviceCategories,
                        search = event.search,
                        services = persistentListOf()
                    )
                }
                loadServices(
                    delayMillis = 300L
                )
            }
            is ServicesViewEvent.ClearSelectedCategories -> {
                val selections = viewState.value.serviceCategories.toMutableList().map {
                    Selectable(
                        selected = false,
                        data = it.data
                    )
                }
                _isLastPage = false
                currentPage = 1
                serviceList.clear()
                setState {
                    copy(
                        serviceCategories = selections.toPersistentList(),
                        services = persistentListOf()
                    )
                }
                loadServices(
                    delayMillis = 300L
                )
            }
            is ServicesViewEvent.ServiceCategorySelected -> {
                val selections = viewState.value.serviceCategories.toMutableList().map {
                    Selectable(
                        selected = it.data == event.category,
                        data = it.data
                    )
                }
                _isLastPage = false
                currentPage = 1
                serviceList.clear()
                setState {
                    copy(
                        serviceCategories = selections.toPersistentList(),
                        services = persistentListOf()
                    )
                }
                loadServices(
                    delayMillis = 300L
                )
            }
            ServicesViewEvent.LoadMore -> {
                loadServices()
            }
            ServicesViewEvent.Reload -> {
                _isLastPage = false
                currentPage = 1
                serviceList.clear()
                setState {
                    copy(
                        services = persistentListOf()
                    )
                }
                loadServices(delayMillis = 1500L)
            }
            ServicesViewEvent.AddServiceCategory -> {

            }
            is ServicesViewEvent.EditService -> {
                setEffect {
                    ServicesViewSideEffect.ShowEditService(
                        service = event.service
                    )
                }
            }
            is ServicesViewEvent.DeleteService -> {
                serviceList.removeAll { it.id == event.id }
                setState { copy(services = serviceList.toPersistentList()) }
            }
        }
    }

    private fun loadServices(
        delayMillis: Long = 0L
    ) {
        if (_isLastPage) {
            return
        }
        setState {
            copy(
                screenState = ServicesState.ScreenState.Loading
            )
        }
        launch(Dispatchers.Default) {
            delay(delayMillis)
            servicesRepository.getServices(
                search = viewState.value.search,
                category = if (viewState.value.serviceCategories.isEmpty()) {
                    ""
                } else {
                    (viewState.value.serviceCategories.firstOrNull { it.selected }?.data?.id ?: "")
                        .toString()
                },
                pageSize = PAGE_SIZE,
                page = currentPage
            ).stateHandle(
                onError = { _, message ->
                    setState {
                        copy(
                            screenState = ServicesState.ScreenState.Error(
                                text = message
                            )
                        )
                    }
                },
                onFailure = {
                    setState {
                        copy(
                            screenState = ServicesState.ScreenState.Error(
                                text = "Произошла неожиданная ошибка"
                            )
                        )
                    }
                },
                onSuccess = { data ->
                    serviceList.addAll(data ?: emptyList())
                    currentPage++
                    setState {
                        copy(
                            services = serviceList.toPersistentList(),
                            screenState = ServicesState.ScreenState.Idle
                        )
                    }
                    _isLastPage = (data?.size ?: 0) < PAGE_SIZE
                }
            )
        }
    }

    private fun getServiceCategories() {
        launch(Dispatchers.Default) {
            servicesRepository.getServiceCategories().stateHandle(
                onError = { _, _ -> },
                onFailure = { },
                onSuccess = { data ->
                    setState {
                        copy(
                            serviceCategories = data?.map {
                                Selectable(
                                    selected = false,
                                    data = it
                                )
                            }?.toPersistentList() ?: persistentListOf()
                        )
                    }
                }
            )
        }
    }

    init {
        getServiceCategories()
        loadServices()
    }

    companion object {
        private const val PAGE_SIZE = 10
    }
}